/** FELVORA — Reportes gerenciales/operativos (F7 A6).
 *
 * feb-2026 · iter 08-02 — Textos neutralizados: título usa `giroNombreCorto(user.giro)`.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { BarChart3, Building2, DollarSign, AlertTriangle, Coins, Receipt } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { giroNombreCorto } from "@/lib/actividad_vulnerable";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";

const mxn = (n) => new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN" }).format(n || 0);
const hoyPeriodo = () => new Date().toISOString().slice(0, 7);

export default function Reportes() {
  const { user } = useAuth();
  const giroCorto = giroNombreCorto(user?.giro);
  const [tab, setTab] = useState("inventario");
  const [periodo, setPeriodo] = useState(hoyPeriodo());
  const [asesores, setAsesores] = useState([]);
  const [inv, setInv] = useState(null);
  const [rent, setRent] = useState(null);
  const [mor, setMor] = useState(null);
  const [com, setCom] = useState(null);
  const [eg, setEg] = useState(null);
  const nombreAsesor = (id) => asesores.find((a) => a.id === id)?.name || id?.slice(0, 8) || "—";

  useEffect(() => {
    api.get("/inmobiliaria/asesores").then((r) => setAsesores(r.data)).catch(() => {});
  }, []);

  const load = () => {
    api.get("/inmobiliaria/reportes/inventario-inmuebles").then((r) => setInv(r.data)).catch(() => setInv(null));
    api.get(`/inmobiliaria/reportes/rentas-cobradas-vs-esperadas`, { params: { periodo } }).then((r) => setRent(r.data)).catch(() => setRent(null));
    api.get("/inmobiliaria/reportes/morosidad-antiguedad").then((r) => setMor(r.data)).catch(() => setMor(null));
    api.get("/inmobiliaria/reportes/comisiones-asesor").then((r) => setCom(r.data)).catch(() => setCom(null));
    api.get(`/inmobiliaria/reportes/egresos-categoria`, { params: { periodo } }).then((r) => setEg(r.data)).catch(() => setEg(null));
  };
  useEffect(() => { load(); }, [periodo]);

  return (
    <div className="p-6 md:p-8 space-y-6" data-testid="reportes-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-semibold text-slate-900">
            <BarChart3 className="mr-2 inline h-6 w-6 text-indigo-700" />
            Reportes · {giroCorto}
          </h1>
          <p className="text-sm text-slate-500">Vista consolidada · 5 reportes agregados de operación.</p>
        </div>
        <div>
          <label className="text-xs text-slate-500">Periodo (afecta rentas y egresos)</label>
          <Input type="month" value={periodo} onChange={(e) => setPeriodo(e.target.value)} data-testid="input-periodo-reportes" />
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="grid w-full grid-cols-3 md:grid-cols-5">
          <TabsTrigger value="inventario" data-testid="tab-inventario"><Building2 className="mr-1 h-4 w-4" /> Inventario</TabsTrigger>
          <TabsTrigger value="rentas" data-testid="tab-rentas"><DollarSign className="mr-1 h-4 w-4" /> Rentas</TabsTrigger>
          <TabsTrigger value="morosidad" data-testid="tab-morosidad"><AlertTriangle className="mr-1 h-4 w-4" /> Morosidad</TabsTrigger>
          <TabsTrigger value="comisiones" data-testid="tab-comisiones"><Coins className="mr-1 h-4 w-4" /> Comisiones</TabsTrigger>
          <TabsTrigger value="egresos" data-testid="tab-egresos"><Receipt className="mr-1 h-4 w-4" /> Egresos</TabsTrigger>
        </TabsList>

        <TabsContent value="inventario">
          <Card>
            <CardHeader><CardTitle className="text-sm">Cartera de inmuebles · Total: {inv?.total ?? "—"}</CardTitle></CardHeader>
            <CardContent className="grid grid-cols-1 gap-4 md:grid-cols-3">
              <div>
                <div className="text-xs font-semibold text-slate-500 mb-2">Por estatus</div>
                {Object.entries(inv?.por_estatus || {}).map(([k, v]) => (
                  <div key={k} className="flex justify-between border-b py-1 text-sm" data-testid={`inv-estatus-${k}`}>
                    <span>{k}</span><b>{v}</b>
                  </div>
                ))}
              </div>
              <div>
                <div className="text-xs font-semibold text-slate-500 mb-2">Por tipo</div>
                {Object.entries(inv?.por_tipo || {}).map(([k, v]) => (
                  <div key={k} className="flex justify-between border-b py-1 text-sm" data-testid={`inv-tipo-${k}`}>
                    <span>{k}</span><b>{v}</b>
                  </div>
                ))}
              </div>
              <div>
                <div className="text-xs font-semibold text-slate-500 mb-2">Por asesor</div>
                {Object.entries(inv?.por_asesor || {}).map(([k, v]) => (
                  <div key={k} className="flex justify-between border-b py-1 text-sm">
                    <span>{nombreAsesor(k)}</span><b>{v}</b>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="rentas">
          <Card>
            <CardHeader><CardTitle className="text-sm">Rentas cobradas vs esperadas · {periodo}</CardTitle></CardHeader>
            <CardContent className="grid grid-cols-2 gap-3 md:grid-cols-4">
              <div className="rounded bg-emerald-50 p-3"><div className="text-xs text-emerald-700">Cobrado</div><div className="text-xl font-semibold" data-testid="rent-cobrado">{mxn(rent?.cobrado_mxn)}</div></div>
              <div className="rounded bg-sky-50 p-3"><div className="text-xs text-sky-700">Esperado</div><div className="text-xl font-semibold" data-testid="rent-esperado">{mxn(rent?.esperado_mxn)}</div></div>
              <div className="rounded bg-amber-50 p-3"><div className="text-xs text-amber-700">Diferencia</div><div className="text-xl font-semibold" data-testid="rent-diff">{mxn(rent?.diferencia_mxn)}</div></div>
              <div className="rounded bg-indigo-50 p-3"><div className="text-xs text-indigo-700">Eficiencia</div><div className="text-xl font-semibold" data-testid="rent-eficiencia">{rent?.eficiencia_cobranza_pct}%</div></div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="morosidad">
          <Card>
            <CardHeader><CardTitle className="text-sm">Morosidad por antigüedad · Contratos morosos: {mor?.totales.contratos_morosos ?? "—"}</CardTitle></CardHeader>
            <CardContent className="grid grid-cols-1 gap-3 md:grid-cols-4">
              {[
                ["0_30", "0-30 días", "emerald"],
                ["31_60", "31-60 días", "amber"],
                ["61_90", "61-90 días", "orange"],
                ["91_plus", "> 90 días", "rose"],
              ].map(([k, l, c]) => (
                <div key={k} className={`rounded bg-${c}-50 p-3 border border-${c}-200`} data-testid={`mor-${k}`}>
                  <div className={`text-xs text-${c}-700`}>{l}</div>
                  <div className="text-xl font-semibold">{mor?.buckets[k].n ?? 0}</div>
                  <div className="text-sm">{mxn(mor?.buckets[k].monto)}</div>
                </div>
              ))}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="comisiones">
          <Card>
            <CardHeader><CardTitle className="text-sm">Comisiones por asesor</CardTitle></CardHeader>
            <CardContent className="p-0">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
                  <tr>
                    <th className="p-2">Asesor</th>
                    <th className="p-2 text-right">Devengada</th>
                    <th className="p-2 text-right">Autorizada</th>
                    <th className="p-2 text-right">Pagada</th>
                    <th className="p-2 text-right">Cancelada</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(com?.por_asesor || {}).map(([aid, k]) => (
                    <tr key={aid} className="border-t" data-testid={`com-${aid}`}>
                      <td className="p-2 font-medium">{nombreAsesor(aid)}</td>
                      <td className="p-2 text-right text-slate-700">{mxn(k.devengada.monto)}</td>
                      <td className="p-2 text-right text-sky-700">{mxn(k.autorizada.monto)}</td>
                      <td className="p-2 text-right text-emerald-700">{mxn(k.pagada.monto)}</td>
                      <td className="p-2 text-right text-rose-700">{mxn(k.cancelada.monto)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="egresos">
          <Card>
            <CardHeader><CardTitle className="text-sm">Egresos por categoría · Total {mxn(eg?.total_mxn)} en {periodo}</CardTitle></CardHeader>
            <CardContent className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <div className="text-xs font-semibold text-slate-500 mb-2">Por categoría</div>
                {Object.entries(eg?.por_categoria || {}).map(([k, v]) => (
                  <div key={k} className="flex justify-between border-b py-1 text-sm" data-testid={`eg-cat-${k}`}>
                    <span className="capitalize">{k}</span>
                    <span><b>{mxn(v.monto)}</b> <span className="text-xs text-slate-500">({v.n})</span></span>
                  </div>
                ))}
              </div>
              <div>
                <div className="text-xs font-semibold text-slate-500 mb-2">Absorbido por</div>
                {Object.entries(eg?.por_absorbe || {}).map(([k, v]) => (
                  <div key={k} className="flex justify-between border-b py-1 text-sm" data-testid={`eg-abs-${k}`}>
                    <span className="capitalize">{k}</span><b>{mxn(v)}</b>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
